import React from 'react';
import { Alert } from 'antd';
import { FlaskConical } from 'lucide-react';
import { useSocialContext } from '../context/social';
import { isProviderEnabled } from '../config/demo';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { BRAND_NAME } from '../config/brand';
import collageBg from '../assets/access/landing-collage.webp';
import step1Image from '../assets/access/landing-step-1-sube-contenido.jpg';
import step2Image from '../assets/access/landing-step-2-suma-views.jpg';
import step3Image from '../assets/access/landing-step-3-recibe-premios.jpg';

// Landing v2 (2026-07-08) — réplica del mock de la diseñadora (Login.png):
// hero con collage de shows LUZU de fondo + overlay violeta en degradé
// (#6D28D9 → #3A1573), CTA TikTok blanco, panel translúcido con los 3 pasos y
// footer gris claro. Header y fila de stats del mock ocultos a pedido de
// Agustín (2026-07-08): las stats eran mockeadas.

const YELLOW = '#FFC53C';

const TikTokIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='currentColor'>
    <path d='M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.87a8.18 8.18 0 0 0 4.78 1.52V6.93a4.85 4.85 0 0 1-1.01-.24z' />
  </svg>
);

const InstagramIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' />
  </svg>
);

const GoogleIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24'>
    <path fill='#4285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' />
    <path fill='#34A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' />
    <path fill='#FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' />
    <path fill='#EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' />
  </svg>
);

// Botón secundario para providers alternativos (IG / Google / fake-dev).
// En prod solo TikTok está habilitado, así que casi nunca se ven.
const SecondaryProviderButton: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void }> = ({
  icon,
  label,
  onClick,
}) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      padding: '10px 22px',
      borderRadius: 999,
      background: 'rgba(255,255,255,0.14)',
      border: '1px solid rgba(255,255,255,0.35)',
      cursor: 'pointer',
      fontWeight: 700,
      fontSize: 14,
      color: 'white',
    }}
  >
    {icon}
    Continuar con {label}
  </button>
);

const STEPS = [
  { title: '1. Sube contenido', image: step1Image },
  { title: '2. Suma views', image: step2Image },
  { title: '3. Recibe premios.', image: step3Image },
];

const FOOTER_LINKS = [
  { label: 'Términos y Condiciones', href: '/terms' },
  { label: 'Política de Privacidad', href: '/privacy' },
];

export const AccessPage: React.FC = () => {
  const { connectProvider, authError, clearAuthError } = useSocialContext();

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', display: 'flex', flexDirection: 'column' }}>
      {/* ── Auth error banner ─────────────────────────────────── */}
      {authError && (
        <Alert
          type='error'
          message={authError}
          closable
          onClose={clearAuthError}
          style={{ width: '100%', maxWidth: 560, margin: '12px auto', borderRadius: 10 }}
        />
      )}

      {/* ── Hero (foto LUZU + tinte violeta) ──────────────────── */}
      <section
        style={{
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: 'clamp(48px, 7vw, 96px) 20px clamp(56px, 8vw, 110px)',
        }}
      >
        {/* capa foto + capa tinte, detrás del contenido */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${collageBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(160deg, rgba(109,40,217,0.74) 0%, rgba(58,21,115,0.86) 100%)',
            zIndex: 1,
          }}
        />

        <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 1120, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h1
            style={{
              color: '#ffffff',
              fontWeight: 800,
              fontSize: 'clamp(32px, 5.5vw, 56px)',
              lineHeight: 1.12,
              margin: '0 0 18px',
              textShadow: '0 2px 18px rgba(30,20,70,0.35)',
            }}
          >
            Compartí contenido. <span style={{ color: YELLOW }}>Ganá premios.</span>
          </h1>

          <p
            style={{
              color: 'rgba(255,255,255,0.88)',
              fontSize: 'clamp(15px, 2vw, 18px)',
              lineHeight: 1.6,
              maxWidth: 520,
              margin: '0 0 34px',
              textShadow: '0 1px 10px rgba(30,20,70,0.3)',
            }}
          >
            Publicá en tus redes sociales y acumulá puntos por el rendimiento de cada post.
            Tu creatividad merece ser recompensada.
          </p>

          {/* CTA principal */}
          {isProviderEnabled('tiktok') && (
            <button
              onClick={() => connectProvider('tiktok')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '17px 40px',
                borderRadius: 999,
                background: '#ffffff',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 17,
                color: '#111',
                boxShadow: '0 8px 28px rgba(20,12,60,0.35)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 34px rgba(20,12,60,0.45)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 28px rgba(20,12,60,0.35)';
              }}
            >
              <TikTokIcon />
              Continuar con TikTok
            </button>
          )}

          {/* Providers secundarios (solo si están habilitados por config) */}
          {(isProviderEnabled('instagram') || isProviderEnabled('google') || FEATURE_FLAGS.ENABLE_FAKE_SOCIAL || isProviderEnabled('fake')) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 14 }}>
              {isProviderEnabled('instagram') && (
                <SecondaryProviderButton icon={<InstagramIcon />} label='Instagram' onClick={() => connectProvider('instagram')} />
              )}
              {isProviderEnabled('google') && (
                <SecondaryProviderButton icon={<GoogleIcon />} label='Google' onClick={() => connectProvider('google')} />
              )}
              {(FEATURE_FLAGS.ENABLE_FAKE_SOCIAL || isProviderEnabled('fake')) && (
                <SecondaryProviderButton icon={<FlaskConical size={20} />} label='FakeSocialMedia (dev)' onClick={() => connectProvider('fake')} />
              )}
            </div>
          )}

          <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 16 }}>
            Al continuar aceptás los{' '}
            <a href='/terms' style={{ color: '#ffffff', textDecoration: 'underline' }}>Términos de uso</a>
            {' '}y la{' '}
            <a href='/privacy' style={{ color: '#ffffff', textDecoration: 'underline' }}>Política de privacidad</a>.
          </span>

          {/* Panel translúcido con los 3 pasos */}
          <div
            style={{
              width: '100%',
              marginTop: 44,
              padding: 'clamp(20px, 3vw, 36px)',
              borderRadius: 24,
              background: 'rgba(28, 22, 48, 0.38)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 'clamp(16px, 2.5vw, 28px)',
            }}
          >
            {STEPS.map(step => (
              <article
                key={step.title}
                style={{
                  borderRadius: 16,
                  background: 'rgba(24, 18, 42, 0.35)',
                  padding: 'clamp(16px, 2vw, 24px)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  textAlign: 'left',
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: 'clamp(18px, 2vw, 23px)',
                    lineHeight: 1.2,
                    textShadow: '0 1px 8px rgba(20,12,50,0.4)',
                  }}
                >
                  {step.title}
                </h3>
                <img
                  src={step.image}
                  alt={step.title}
                  loading='lazy'
                  style={{
                    width: '100%',
                    aspectRatio: '512 / 286',
                    objectFit: 'cover',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.35)',
                    display: 'block',
                  }}
                />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer
        style={{
          background: '#F1F2F6',
          borderRadius: '24px 24px 0 0',
          padding: 'clamp(24px, 4vw, 40px) clamp(24px, 5vw, 72px)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'clamp(16px, 3vw, 32px)',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 18, color: '#111', letterSpacing: '-0.01em' }}>
          {BRAND_NAME}
        </div>

        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(16px, 3vw, 32px)' }}>
          {FOOTER_LINKS.map(link => (
            <a key={link.href} href={link.href} style={{ color: '#444', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>
              {link.label}
            </a>
          ))}
        </nav>

        <div style={{ color: '#8a8f9c', fontSize: 13 }}>
          © {new Date().getFullYear()} {BRAND_NAME}
        </div>
      </footer>
    </div>
  );
};
